import React, { useState, useEffect} from 'react';
import {
    Typography,
    makeStyles,
    TextField,
    Grid,
    Button, CardContent, InputAdornment
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useLocation, useNavigate } from "react-router-dom";
import ConnectorFormFile from "./File";
import ConnectorFormShipperMapping from "./ShipperMapping";
import getApiConnectorRoute from "../apiRoutes";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { fab } from '@fortawesome/free-brands-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import LinearProgress from "@material-ui/core/LinearProgress";
import {Alert} from "@material-ui/lab";
import ConnectorFormCodPaymentMethods from "./CodPaymentMethods";
import ConnectorFormStatusAfterExport from "./StatusAfterExport";
import ConnectorFormStatusDelivered from "./StatusDelivered";
import ConnectorFormCountOfPackages from "./CountOfPackages";
import { getShipperSelectOptions } from 'helpers';
import getApiRoute from 'routes/apiRoutes';
import {
    buildProcessorSettings,
    applyProcessorLoadedSettings,
    getProcessorCreateUrlSuffix,
    getProcessorComponent,
    getProcessorEntry,
    processorShowUrlField,
    processorShowDeliveredStatus,
    processorShowCountOfPackages,
    processorShowExportStatus,
    processorShowDownloadError,
    processorShowCarrierType,
} from "./processors/registry";
library.add(fab);

const useStyles = makeStyles((theme) => ({
    root: {},
    button: {
        marginLeft: '10px'
    },
    alert: {
        marginBotton: '15px'
    }
}));

const ConnectorForm = ({ AppValues, secureFetch, ...rest }) => {
    const classes = useStyles();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation('connector');
    const [submitted, setSubmitted] = useState(false);
    const [id, setId] = useState(state.id) // conenctor ID
    const [loading, setLoading] = useState(false);
    const [processors, setProcessors] = useState([]);
    const [processor, setProcessor] = useState(state.processor) // {id: 0, label: ''}
    const [name, setName] = useState("");
    const [fileUrl, setFileUrl] = useState("");
    const [processorSettings, setProcessorSettings] = useState({});
    const [oauthUrl, setOAuthUrl] = useState("");
    const [tokenUrl, setTokenUrl] = useState("");
    const [shipperMappings, setShipperMappings] = useState([]);
    const [shipperList, setShipperList] = useState([]);
    const [connectorShipperList, setConnectorShipperList] = useState([]);
    const [connectorPaymentMethods, setConnectorPaymentMethods] = useState([]);
    const [codPaymentMethods, setCodPaymentMethods] = useState([]);
    const [connectorExportStatuses, setConnectorExportStatuses] = useState([]);
    const [exportStatus, setExportStatus] = useState(null);
    const [connectorDeliveredStatuses, setConnectorDeliveredStatuses] = useState([]);
    const [deliveredStatus, setDeliveredStatus] = useState(null);
    const [shipperDownloadMessage, setShipperDownloadMessage] = useState('');
    const [carriers, setCarriers] = useState([]);
    const [shipperMappingErrors, setShipperMappingErrors] = useState({});

    useEffect(() => {
        const userShipperShortcuts = AppValues.user.user_shipper_price_lists.map(uspl => uspl.shipper.shortcut);
        setShipperList(AppValues.shippers.filter(sh => userShipperShortcuts.includes(sh.shortcut)));
    }, [AppValues]);

    const handleFormSubmit = (event, action) => {
        setLoading(true);
        event.preventDefault();
        const payloadProcessorSettings = buildProcessorSettings(processor?.id, processorSettings);
        const data = {
            name,
            path: fileUrl,
            settings: {
                ...payloadProcessorSettings,
                shipperMappings: shipperMappings.length ? shipperMappings : [],
                cashOnDeliveryMethods: codPaymentMethods.length ? codPaymentMethods : [],
                exportStatus,
                deliveredStatus,
            },
        };
        Object.keys(data.settings).forEach((k) => {
            if (data.settings[k] === undefined) delete data.settings[k];
        });

        const cleanedMappings = shipperMappings.length ? shipperMappings.map(m => {
            if (!processorShowCarrierType(processor?.id)) {
                const { carrier_type, ...rest } = m;
                return rest;
            }
            return m;
        }) : [];
        data.settings.shipperMappings = cleanedMappings;
        data.settings.cashOnDeliveryMethods = codPaymentMethods.length ? codPaymentMethods : [];
        data.settings.exportStatus = exportStatus;
        data.settings.deliveredStatus = deliveredStatus;

        setShipperMappingErrors({});

        const url = id
            ? getApiConnectorRoute('update', [id])
            : getApiConnectorRoute('create', getProcessorCreateUrlSuffix(processor?.id, action));
        secureFetch(url, data, id ? 'PUT' : 'POST', false)
            //.then(result => result.json())
            .then(response => {
                if (response.status && (response.status === 'created' || response.status === 'updated')) {
                    if (action === 'continue') {
                        setConnectorShipperList([]);
                        setConnectorPaymentMethods([]);
                        setConnectorExportStatuses([]);
                        setConnectorDeliveredStatuses([]);
                        setShipperMappings([]);
                        setCodPaymentMethods([]);
                        setExportStatus(null);
                        setDeliveredStatus(null);
                        setProcessorSettings(prev => ({ ...prev, connectorCountOfPackages: '' }));
                        setSubmitted(true);
                        setLoading(false);
                        if (response.id){
                            setId(response.id);
                        }
                    } else {
                        navigate('/app/connector', {
                            state: {
                            }
                        });
                    }
                } else {
                    setConnectorShipperList(response.shippers);
                    setConnectorPaymentMethods(response.payment_methods);
                    setConnectorExportStatuses(response.order_statuses);
                    setConnectorDeliveredStatuses(response.order_statuses);
                    setShipperMappings([{ neoship_shipper: '', eshop_shipper: '' }]);
                    setCodPaymentMethods([]);
                    setExportStatus(null);
                    setDeliveredStatus(null);
                    setProcessorSettings(prev => ({ ...prev, connectorCountOfPackages: '' }));
                    setSubmitted(true);
                    setLoading(false);
                }
            })
            .catch(errorResponse => {
                setLoading(false);
                if (!errorResponse) return;

                const mappingErrors = {};
                const mappings = errorResponse?.settings?.shipperMappings;
                if (mappings) {
                    Object.keys(mappings).forEach(index => {
                        const mapping = mappings[index];
                        if (!mapping || typeof mapping !== 'object') return;
                        Object.keys(mapping).forEach(field => {
                            const msg = Object.values(mapping[field] || {}).find(v => typeof v === 'string');
                            if (msg) {
                                mappingErrors[index] = { ...mappingErrors[index], [field]: msg };
                            }
                        });
                    });
                }

                if (Object.keys(mappingErrors).length > 0) {
                    setShipperMappingErrors(mappingErrors);
                }
            });
    }

    const handleButtonBackClick = (event) => { 
        event.preventDefault();
        navigate('/app/connector', {
            state: {
            }
        });
    }

    const loadData = () => {
        if (id) {
            secureFetch(getApiConnectorRoute('read', [id]))
                .then(data => {
                    if (data) {
                        setId(data.id);
                        setProcessor({id: data.processor, label: ''});
                        setName(data.name);
                        setFileUrl(data.path);
                        setShipperMappings(data.settings?.shipperMappings ?? [])
                        setCodPaymentMethods(data.settings?.cashOnDeliveryMethods ?? [])
                        setExportStatus(data.settings?.exportStatus ?? null)
                        setDeliveredStatus(data.settings?.deliveredStatus ?? null)
                        applyProcessorLoadedSettings(data.processor, data.settings, setProcessorSettings);
                    }
                });
        } else {
            setName('');
            setFileUrl('');
            setProcessorSettings({});
            setOAuthUrl('');
            setTokenUrl('');
            setShipperMappings([]);
        }
    }

    const loadShippers = (addEmpty = false) => {
        if (id) {
            secureFetch(getApiConnectorRoute('shippers', [id]))
                .then(response => {
                    if (response.length){
                        setConnectorShipperList(response);
                        setShipperDownloadMessage('');
                        if(addEmpty){
                            setShipperMappings([{ neoship_shipper: '', eshop_shipper: '' }]);
                        }
                    } else if (processorShowDownloadError(processor?.id, 'shippers')) {
                        setShipperDownloadMessage(t('messages.error_shippers_download'));
                    }
                });
        }
    }

    const loadPaymentMethods = () => {
        if (id) {
            secureFetch(getApiConnectorRoute('payment_methods', [id]))
                .then(response => {
                    if (response.length){
                        setConnectorPaymentMethods(response);
                        setShipperDownloadMessage('');
                    } else if (processorShowDownloadError(processor?.id, 'paymentMethods')) {
                        setShipperDownloadMessage(t('messages.error_payment_methods_download'));
                    }
                });
        }
    }

    const loadOrderStatuses = () => {
        if (id) {
            secureFetch(getApiConnectorRoute('statuses', [id]))
                .then(response => {
                    if (response.length){
                        setConnectorExportStatuses(response);
                        setConnectorDeliveredStatuses(response);
                        setShipperDownloadMessage('');
                    } else if (processorShowDownloadError(processor?.id, 'status')) {
                        setShipperDownloadMessage(t('messages.error_order_statuses_download'));
                    }
                });
        }
    }

    const loadProcessors = () => {
        secureFetch(getApiConnectorRoute('processors'))
            //.then(result => result.json())
            .then(data => {
                setProcessors(data);
                setProcessor(data[0]);
            });
    }

    const loadCarriers = () => {
        secureFetch(getApiRoute('carrier_available'))
            .then(data => {
                if (Array.isArray(data)) {
                    setCarriers(data);
                }
            });
    }

    useEffect(() => {
        loadShippers();
        loadPaymentMethods();
        loadOrderStatuses();
        loadData();
        loadCarriers();
        if (!id) {
            loadProcessors();
        }
    }, []);

    return (
        <>
                <Typography variant="h1" gutterBottom >
                    {id ? t("title.edit_connector"): t("title.new_connector") }
                </Typography>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            { loading && <LinearProgress style={{ marginBottom: '1rem' }} />}
                        </Grid>
                        {!id &&
                            <Grid item xs={12}>
                                <Autocomplete
                                    id="combo-box-processors"
                                    disabled={submitted}
                                    options={processors}
                                    value={processor || ''}
                                    getOptionLabel={(option) => option.label}
                                    renderInput={(params) => <TextField {...params} label="Processor" />}
                                    onChange={(event, value) => setProcessor(value) }
                                />
                            </Grid>
                        }
                        {processor &&
                            <React.Fragment>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="connector-name"
                                        label={t('label.name')}
                                        variant="outlined"
                                        value={ name }
                                        onChange={ (event) => setName(event.target.value) }
                                        InputProps={{
                                            startAdornment: <InputAdornment style={{opacity: 0.5}} position="start"><FontAwesomeIcon icon={['fab', 'hubspot']} /></InputAdornment>,
                                        }}
                                        disabled={submitted}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        {processorShowUrlField(processor.id) && (
                                            <ConnectorFormFile fileUrl={fileUrl} setFileUrl={(value) => setFileUrl(value)} submitted={submitted}/>
                                        )}
                                        {(() => {
                                            const ProcessorCredentials = getProcessorComponent(processor.id);
                                            const entry = getProcessorEntry(processor.id);
                                            return (
                                                ProcessorCredentials &&
                                                <ProcessorCredentials
                                                    processorSettings={processorSettings}
                                                    setProcessorSettings={setProcessorSettings}
                                                    submitted={submitted}
                                                    keyDisabled={entry.keyDisabled}
                                                />
                                            );
                                        })()}
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        }
                        { !!shipperDownloadMessage &&
                            <Grid item xs={12}>
                                <Alert severity="error" className={classes.alert}>{shipperDownloadMessage}</Alert>
                            </Grid>
                        }
                        {(submitted || id) && !connectorShipperList.length &&
                            <Grid item xs={12}>
                                <Button variant="contained" color="primary" type="button" onClick={() => {loadData(); loadShippers(true); loadPaymentMethods(); loadOrderStatuses();}} >{t('button.add_shipper')}</Button>
                            </Grid>
                        }
                        { !!connectorShipperList.length &&
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="h4">{t('title.shipper_mappings')}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ConnectorFormShipperMapping
                                            neoshipShippers={ getShipperSelectOptions(shipperList, processor?.id) }
                                            eshopShippers={ connectorShipperList }
                                            shipperMappings={ shipperMappings }
                                            setShipperMappings={ setShipperMappings }
                                            carriers={ carriers }
                                            errors={ shipperMappingErrors }
                                            showCarrierType={ processorShowCarrierType(processor?.id) }
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                        { !!connectorPaymentMethods.length &&
                        <Grid item xs={4}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">{t('title.payment_methods')}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <ConnectorFormCodPaymentMethods
                                        connectorPaymentMethods={ connectorPaymentMethods }
                                        codPaymentMethods={ codPaymentMethods }
                                        setCodPaymentMethods={ setCodPaymentMethods }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        }
                        { !!connectorExportStatuses.length && processor && processorShowExportStatus(processor.id) &&
                        <Grid item xs={6}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">{t('title.order_statuses')}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <ConnectorFormStatusAfterExport
                                        connectorExportStatuses={ connectorExportStatuses }
                                        exportStatus={ exportStatus }
                                        setExportStatus={ setExportStatus }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        }
                        { !!connectorDeliveredStatuses.length && processor && processorShowDeliveredStatus(processor.id) &&
                            <Grid item xs={6}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5">{t('title.delivered_order_statuses')}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <ConnectorFormStatusDelivered
                                            connectorDeliveredStatuses={ connectorDeliveredStatuses }
                                            deliveredStatus={ deliveredStatus }
                                            setDeliveredStatus={ setDeliveredStatus }
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                        { !!connectorPaymentMethods.length && processor && processorShowCountOfPackages(processor.id) &&
                            <Grid item xs={4}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="h4">{t('title.connector_count_of_packages')}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ConnectorFormCountOfPackages value={ processorSettings.connectorCountOfPackages ?? '' } setCountOfPackages={ (value) => setProcessorSettings(prev => ({ ...prev, connectorCountOfPackages: value })) } submitted={submitted}/>
                                        {/*<TextField fullWidth id="connector-count-of-packages" label={t('label.connector_count_of_packages')} variant="outlined"value={ countOfPackages }  onChange={(event) => setCountOfPackages(event.target.value)} />*/}
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <Grid container justify="flex-end">
                                <Button variant="contained" color="secondary" onClick={(event) => handleButtonBackClick(event) } type="button" className={classes.button}>
                                    { t('button.back') }
                                </Button>
                                {
                                    submitted || id ?
                                        <Button variant="contained" color="primary" type="button" disabled={ !processor } className={classes.button} onClick={(event) => handleFormSubmit(event, 'save')}>
                                            {t('button.save')}
                                        </Button> :
                                        <Button variant="contained" color="primary" type="button" disabled={ !processor } className={classes.button} onClick={(event) => handleFormSubmit(event, 'continue')}>
                                            {t('button.continue')}
                                        </Button>
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
        </>
    )
}

export default ConnectorForm;